import { MDBDropdown, MDBDropdownLink, MDBDropdownMenu, MDBDropdownToggle, MDBInput } from 'mdb-react-ui-kit';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import "./ShareMenu.css"
import AcceptModal from './AcceptModal';
import { AppContext } from '../App';
import { Roles} from 'konect-model';
import UserService from '../../services/user-service';
import { useDebounce } from '../../services/custom-hooks';

interface ShareMenuProps {
    shareMenuUsers: { username: string; role: number; }[];
    setShareMenuUsers: (state: { username: string; role: number; }[]) => void;
    shareMenuSearchResults: string[];
    setShareMenuSearchResults: (state: string[]) => void;
    shareMenuSearchInput: string;
    setShareMenuSearchInput: (state: string) => void;
}

const TYPE_NAMES = {
    [Roles.OWNER]: "OWNER",
    [Roles.MAINTAINER]: "MAINTAINER",
    [Roles.SPECTATOR]: "SPECTATOR"
}

const TYPE_COLOR = {
    [Roles.OWNER]: "blue",
    [Roles.MAINTAINER]: "orange",
    [Roles.SPECTATOR]: "gray"
}

const getProjectOwners = (users: any[]) => {
    return users.filter((user) => user.role === Roles.OWNER);
}

export default function ShareMenu(props: ShareMenuProps) {
    const { project, user } = useContext(AppContext);
    const users = props.shareMenuUsers;
    const setUsers = props.setShareMenuUsers;
    const allowedToEdit = project.users.find(entry => entry.username === user.username && entry.role === Roles.OWNER) || users.length <= 0 || users.find(u => u.username === user.username && u.role === Roles.OWNER);

    // search bar states
    const inputValue = props.shareMenuSearchInput;
    const setInputValue = props.setShareMenuSearchInput;
    const userList = props.shareMenuSearchResults;
    const setUserList = props.setShareMenuSearchResults;
    const userListRef = useRef(userList);
    useEffect(() => { userListRef.current = userList }, [userList]);
    const [showSearchResults, setShowSearchResults] = useState(false);
    const showSearchResultsRef = useRef(showSearchResults);
    useEffect(() => { showSearchResultsRef.current = showSearchResults }, [showSearchResults]);

    // search result states/refs
    const inputSectionRef = useRef<HTMLDivElement>(null);
    const usResultListRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState<number>(100);
    const [selected, setSelected] = useState(-1);
    const selectedRef = useRef(selected);
    useEffect(() => { selectedRef.current = selected }, [selected])

    const checkPosition = () => {
        if (inputSectionRef.current) setWidth(inputSectionRef.current.getBoundingClientRect().width);
    };

    useEffect(() => {
        checkPosition();
        window.addEventListener('resize', checkPosition);

        // handle click outside of the menu/input
        const handleClick = (event :any) => {
            if (usResultListRef.current && !usResultListRef.current.contains(event.target)) setShowSearchResults(false);
        }
        window.addEventListener('mousedown', handleClick);

        const handleKeyDown = (event: any) => {
            if (event.key === 'ArrowUp' && showSearchResultsRef.current) {
                let target = -1;
                if (selectedRef.current > 0) target = selectedRef.current - 1;
                else target = userListRef.current.length - 1;
                if (target >= 0) {
                    setSelected(target);
                    const div = document.getElementById(`usOption_${target}`);
                    if (div) div.scrollIntoView({block: "end", inline: "nearest"});
                }
            }

            else if (event.key === 'ArrowDown' && showSearchResultsRef.current) {
                let target = -1;
                if (selectedRef.current < userListRef.current.length - 1) target = selectedRef.current + 1;
                else target = 0;
                if (target >= 0) {
                    setSelected(target);
                    const div = document.getElementById(`usOption_${target}`);
                    if (div) div.scrollIntoView({block: "end", inline: "nearest"});
                }
            }

            else if (event.key === 'Enter' && showSearchResultsRef.current) {
                if (selectedRef.current >= 0) {
                    addUser(userListRef.current[selectedRef.current], Roles.SPECTATOR);
                }
            }
        }
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('resize', checkPosition);
            window.removeEventListener('mousedown', handleClick);
            window.removeEventListener('keydown', handleKeyDown);
        }
        // Event listeners set up once on mount. Functions access current values via refs.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        const safeUsers = Array.isArray(project.users) ? project.users : [];
        setUsers([...safeUsers]);
        // setUsers is a stable setState function and doesn't need to be in dependencies
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project])

    const debouncedQuery = useDebounce(inputValue, 400);

    const [loading, setLoading] = useState(false);
    const searchUsername = useCallback(async (search:string) => {
        if (search.length > 1) {
            setLoading(true);
            let usernames = await UserService.searchUsers(user, search);
            setLoading(false);
            if (usernames && usernames.length > 0) setShowSearchResults(true);
            setUserList(usernames ? usernames.filter((username:string) => !new Set(users.map(user => user.username)).has(username)) : []);
        } else {
            setUserList([]);
            setShowSearchResults(false);
        }
    }, [user, users, setUserList, setShowSearchResults]);

    useEffect(() => {
        searchUsername(debouncedQuery);
    }, [debouncedQuery, searchUsername]);

    const onFocus = () => {
        checkPosition();
        if (inputValue) searchUsername(inputValue);
    }

    const addUser = async (entry:string, role:Roles) => {
        setUsers([...users, { username: entry, role: users.length > 0 ? role : Roles.OWNER }]);
        setShowSearchResults(false);
        setInputValue("");
    }

    const removeUser = (user: { username: string, role: Roles }) => {
        setUsers(users.filter((u) => u.username !== user.username));
    }


    const getPermissionIcon = (user: { username: string, role: Roles }) => {
        const name = TYPE_NAMES[user.role];
        const owners = getProjectOwners(users);
        const enabled = owners.length > 1;
        //@ts-ignore
        let entries = Object.values(Roles).filter((t, index) =>
            index >= Object.keys(Roles).length / 2 && t !== user.role && (user.role !== Roles.OWNER || enabled)).map((t) => (
                //@ts-ignore
                <MDBDropdownLink key={t} className={"me-2 access-type"} onClick={() => { user.role = t; setUsers([...users]) }} style={{ color: TYPE_COLOR[t], borderColor: TYPE_COLOR[t], backgroundColor: TYPE_COLOR[t] }}>{TYPE_NAMES[t]}
                </MDBDropdownLink>
            )).reverse()

        return ((user.role !== Roles.OWNER || enabled) && allowedToEdit ? <MDBDropdown dropleft>
            <MDBDropdownToggle tag='button' className={"me-2 access-type "}
                style={{ color: TYPE_COLOR[user.role], borderColor: TYPE_COLOR[user.role], backgroundColor: TYPE_COLOR[user.role] }}>
                {name}
            </MDBDropdownToggle>
            <MDBDropdownMenu className='access-menu' align='end'
                style={
                    {
                        display: "flex",
                        minWidth: "0px",
                        boxShadow: "none",
                        borderRadius: "20px"
                    }
                }>
                {entries}
            </MDBDropdownMenu>
        </MDBDropdown> :
            <div className={"access-type"}
                style={
                    {
                        color: TYPE_COLOR[user.role],
                        borderColor: TYPE_COLOR[user.role],
                        backgroundColor: TYPE_COLOR[user.role]
                    }
                }>
                {name}
            </div>)
    }


    return <div className="shareMenuModal">
        <div className="searchWrapper" ref={inputSectionRef} style={{zIndex: showSearchResults ? "1062" : "auto"}}>
            <div style={{ position: "relative" }}>
                <MDBInput label="Search User" value={inputValue} onChange={(e:any) => {setInputValue(e.target.value)}} onFocus={onFocus} />
                {loading && <i className={"fas fa-spinner accept-modal-spinner share-spinner"} />}
            </div>
            {showSearchResults && <div className="usResultList" style={{ width: width, maxHeight: "120px" }} ref={usResultListRef}>
                {userList.map((entry:string, index) => {
                    return <div className="usResultEntry" id={`usOption_${index}`} key={index} onClick={() => {addUser(entry, Roles.SPECTATOR)}} style={selected === index ? {filter: "brightness(0.90)"} : {}} onMouseEnter={() => setSelected(index)}>
                        {entry}
                        <div className="d-flex gap-1">
                            <div
                                className={"access-type"}
                                style={{ color: TYPE_COLOR[Roles.OWNER], borderColor: TYPE_COLOR[Roles.OWNER], backgroundColor: TYPE_COLOR[Roles.OWNER] }} 
                                onClick={(e:any) => { e.stopPropagation(); addUser(entry, Roles.OWNER) }}>
                                    {TYPE_NAMES[2]}
                            </div>
                            <div
                                className={"access-type"}
                                style={{ color: TYPE_COLOR[Roles.MAINTAINER], borderColor: TYPE_COLOR[Roles.MAINTAINER], backgroundColor: TYPE_COLOR[Roles.MAINTAINER] }} 
                                onClick={(e:any) => { e.stopPropagation(); addUser(entry, Roles.MAINTAINER) }}>
                                    {TYPE_NAMES[Roles.MAINTAINER]}
                            </div>
                            <div
                                className={"access-type"}
                                style={{ color: TYPE_COLOR[Roles.SPECTATOR], borderColor: TYPE_COLOR[Roles.SPECTATOR], backgroundColor: TYPE_COLOR[Roles.SPECTATOR] }} 
                                onClick={(e:any) => { e.stopPropagation(); addUser(entry, Roles.SPECTATOR) }}>
                                    {TYPE_NAMES[Roles.SPECTATOR]}
                            </div>
                        </div>
                    </div>
                })}
            </div>}
        </div>

        <div className="sharedUsersList">
        {users.map((u, index) =>
            <div className='access-user-entry' key={user.username + index}>
                <span className='name d-flex align-items-center me-2'>
                    {u.username}
                    {user.username === u.username && <span className='access-type you'>YOU</span>}
                </span>
                <span className='d-flex align-items-center'>
                    {getPermissionIcon(u)}
                    {((u.role !== Roles.OWNER || getProjectOwners(users).length > 1) && allowedToEdit) && 
                    <AcceptModal onAccept={() => { removeUser(u) }} imgType='trash'
                        />}
                </span>
            </div>
        )}
        </div>
    </div>

}
